import { findHeroCustomFieldMatch } from "@/components/hero/heroCustomFieldUtils";
import type { BestellnummerQuelle } from "@/lib/bestellnummer";
import {
  projectWorkflowStatus,
  type Order,
  type AuftragTimesheetSummary,
  type AuftragTimesheetNumber,
} from "@/lib/projects";
import {
  auftragsartAppBadgeKind,
  formatAuftragsartAppLabel,
  isAuftragsartHeroFieldLabel,
  type AuftragsartAppBadgeKind,
} from "@/pages/projekte/auftragsartApp";
import {
  formatHours,
  orderUsesAppKalkStunden,
  resolveAppKalkStunden,
  splitAppKalkStunden,
} from "@/pages/projekte/hoursUsage";

function normalizeCfLabel(label: string | null | undefined): string {
  return String(label || "")
    .trim()
    .toLowerCase()
    .replace(/[\s.\-_]+/g, "");
}

function isWorkshopHoursHeroFieldLabel(label: string | null | undefined): boolean {
  const key = normalizeCfLabel(label);
  return key === "kalkwerkstattstunden" || key === "werkstattstunden";
}

function isMontageHoursHeroFieldLabel(label: string | null | undefined): boolean {
  const key = normalizeCfLabel(label);
  return key === "kalkmontagestunden" || key === "montagestunden";
}

/**
 * Definitionen der Projekt-Tabelle.
 *
 * Pflichtspalte ist `auftragsnummer`. Die Aktionsspalten `open`, `hero` und
 * `cover_print_default` werden in der Tabelle immer am rechten Rand fixiert
 * dargestellt. Der eigentliche Klick auf eine Zeile öffnet weiterhin die
 * klassische Detailansicht. Hero-Custom-Fields werden dynamisch ergänzt
 * (`cf_<schemaId>_<uuid>`).
 */

export const PROJECT_REQUIRED_COLUMNS = ["auftragsnummer"] as const;

/** Spalten, die in der Aktionsspalte rechts (sticky) gerendert werden. */
export const PROJECT_ACTION_COLUMNS = [
  "hero",
  "cover_print_default",
  "open",
] as const;

export type ProjectActionColumnKey = (typeof PROJECT_ACTION_COLUMNS)[number];

export function isProjectActionColumn(key: string): key is ProjectActionColumnKey {
  return (PROJECT_ACTION_COLUMNS as readonly string[]).includes(key);
}

export const PROJECT_HIDDEN_BY_DEFAULT_COLUMNS: string[] = [
  "cover_print_default",
  "booked_hours",
  "historische_rechnungsnummer",
  "historische_lieferscheinnummer",
  "historische_info",
];

export const PROJECT_STATIC_COLUMNS = [
  "auftragsnummer",
  "kunde",
  "ansprechpartner",
  "ort",
  "ort_zusatz",
  "beschreibung",
  "historische_rechnungsnummer",
  "historische_lieferscheinnummer",
  "historische_info",
  "status",
  "execution_class",
  "bestellnummer",
  "kundenreferenz",
  "leitweg_id",
  "hours_usage",
  "booked_hours",
  "timesheets",
  "hero",
  "cover_print_default",
  "open",
] as const;

export type ProjectStaticColumnKey = (typeof PROJECT_STATIC_COLUMNS)[number];

/** Server-seitig sortierbare Spalten (siehe api/auftraege.py `_AUFTRAEGE_SORT_SQL`). */
export const PROJECT_SORTABLE_COLUMNS = new Set<string>([
  "auftragsnummer",
  "kunde",
  "ansprechpartner",
  "ort",
  "ort_zusatz",
  "beschreibung",
  "historische_rechnungsnummer",
  "historische_lieferscheinnummer",
  "historische_info",
  "status",
  "execution_class",
  "bestellnummer",
  "kundenreferenz",
  "leitweg_id",
]);

export const PROJECT_STATIC_COLUMN_LABELS: Record<ProjectStaticColumnKey, string> = {
  auftragsnummer: "Projekt",
  kunde: "Kunde",
  ansprechpartner: "Ansprechpartner",
  ort: "Ort",
  ort_zusatz: "Ort Zusatz",
  beschreibung: "Beschreibung",
  historische_rechnungsnummer: "Rechnungsnr.",
  historische_lieferscheinnummer: "Lieferscheinnr.",
  historische_info: "Allg. Infos",
  status: "Status",
  execution_class: "EXC",
  bestellnummer: "Bestellnummer",
  kundenreferenz: "Kundenreferenz",
  leitweg_id: "Leitweg-ID",
  hours_usage: "Verbrauch",
  booked_hours: "Gebuchte Stunden",
  timesheets: "NW-Nr.",
  hero: "Hero-Link",
  cover_print_default: "Deckblatt (Standarddrucker)",
  open: "Ordner öffnen",
};

/** Mindest-/Maximalbreiten für die Spalten der Projekt-Tabelle (in px). */
export const PROJECT_COL_MIN_WIDTH = 72;
export const PROJECT_COL_MAX_WIDTH = 720;
/** Fixe Breite der Aktionsspalten (Icons), passt zu klassischer Optik. */
export const PROJECT_ACTION_COL_WIDTH = 44;

export interface HeroCustomField {
  schemaId: string;
  uuid: string;
  label: string;
}

export function heroCustomFieldKey(field: HeroCustomField): string {
  return `cf_${field.schemaId}_${field.uuid}`;
}

export function heroCustomFieldLabel(key: string, fields: HeroCustomField[]): string | null {
  const field = fields.find((f) => heroCustomFieldKey(f) === key);
  return field ? field.label : null;
}

/**
 * Liste aller verfügbaren Spalten in stabiler Reihenfolge:
 * Datenspalten → Hero-Custom-Fields → Aktionsspalten (immer ganz rechts).
 */
export function getAvailableColumns(heroFields: HeroCustomField[]): string[] {
  const dataCols = PROJECT_STATIC_COLUMNS.filter(
    (c) => !isProjectActionColumn(c),
  );
  return [
    ...dataCols,
    ...heroFields.map(heroCustomFieldKey),
    ...PROJECT_ACTION_COLUMNS,
  ];
}

export function getDefaultColumns(heroFields: HeroCustomField[]): string[] {
  return getAvailableColumns(heroFields).filter(
    (c) => !PROJECT_HIDDEN_BY_DEFAULT_COLUMNS.includes(c),
  );
}

export function getColumnLabel(key: string, heroFields: HeroCustomField[]): string {
  if (key in PROJECT_STATIC_COLUMN_LABELS) {
    return PROJECT_STATIC_COLUMN_LABELS[key as ProjectStaticColumnKey];
  }
  const heroLabel = heroCustomFieldLabel(key, heroFields);
  return heroLabel ?? key;
}

/**
 * Bringt eine vom Server geladene Auswahl in eine konsistente Reihenfolge:
 * - Pflichtspalten erzwingen
 * - `ort_zusatz` direkt hinter `ort`
 * - Aktionsspalten immer am Ende (in stabiler Reihenfolge)
 */
export function normalizeColumnSelection(
  selection: string[] | undefined | null,
  available: string[],
): string[] {
  const out: string[] = [];
  if (Array.isArray(selection)) {
    for (const col of selection) {
      if (typeof col !== "string") continue;
      const trimmed = col.trim();
      if (!trimmed || !available.includes(trimmed)) continue;
      if (!out.includes(trimmed)) out.push(trimmed);
    }
  }
  for (const required of PROJECT_REQUIRED_COLUMNS) {
    if (available.includes(required) && !out.includes(required)) out.unshift(required);
  }
  if (available.includes("ort_zusatz") && !out.includes("ort_zusatz")) {
    const ortIdx = out.indexOf("ort");
    if (ortIdx >= 0) out.splice(ortIdx + 1, 0, "ort_zusatz");
    else out.push("ort_zusatz");
  }
  // Neue Spalte Execution Class bei vorhandenen Spalten-Layouts sichtbar machen.
  if (available.includes("execution_class") && !out.includes("execution_class")) {
    const statusIdx = out.indexOf("status");
    if (statusIdx >= 0) out.splice(statusIdx + 1, 0, "execution_class");
    else out.push("execution_class");
  }
  // Native Referenzfelder bei vorhandenen Layouts ergänzen (ersetzen Hero-CF).
  for (const col of ["bestellnummer", "kundenreferenz"] as const) {
    if (available.includes(col) && !out.includes(col)) {
      const beschIdx = out.indexOf("beschreibung");
      if (beschIdx >= 0) out.splice(beschIdx + 1, 0, col);
      else out.push(col);
    }
  }

  // Aktionsspalten an das Ende verschieben, in stabiler Reihenfolge.
  const withoutActions = out.filter((c) => !isProjectActionColumn(c));
  const orderedActions = PROJECT_ACTION_COLUMNS.filter((c) => out.includes(c));
  return [...withoutActions, ...orderedActions];
}

/* ───────────────────────── Cell-Rendering ───────────────────────── */

export type TimesheetStatus =
  | "draft"
  | "ready"
  | "signed"
  | "invoice_draft"
  | "invoice_created"
  | "invoice_paid"
  | "archived";

export interface TimesheetBadge {
  label: string;
  status: TimesheetStatus | string;
}

export interface CellValue {
  /** Plain-Text-Wert (für Datenspalten). `null` → "—". */
  value: string | null;
  /** Monospace-Darstellung (z. B. Auftragsnummer). */
  mono?: boolean;
  /**
   * Optionaler "spezieller" Inhalt, den die Tabelle direkt rendern soll.
   * Aktuell genutzt für Stundennachweis-Badges.
   */
  badges?: TimesheetBadge[];
  /** Workflow-Status-Key für farbige Status-Badges. */
  statusKey?: string;
  /** Optionaler Zweitstatus (z. B. Angeboten unter Terminiert). */
  secondaryStatusKey?: string;
  secondaryStatusLabel?: string;
  /** App-abgeleitete Auftragsart → Badge statt Plain-Text. */
  auftragsartBadge?: AuftragsartAppBadgeKind;
  /** Execution Class Badge (EXC1/EXC2). */
  executionClassBadge?: boolean;
  /** Herkunft der Bestellnummer (Kundenregel / manuell). */
  bestellnummerQuelle?: BestellnummerQuelle;
}

export function normalizeTimesheetStatus(raw: string | null | undefined): TimesheetStatus | string {
  const key = String(raw || "").trim().toLowerCase();
  switch (key) {
    case "draft":
    case "":
      return "draft";
    case "ready":
    case "ready_for_signature":
      return "ready";
    case "signed":
    case "completed":
      return "signed";
    case "invoice_draft":
    case "invoice-draft":
      return "invoice_draft";
    case "invoice_created":
    case "invoice-created":
    case "invoice":
    case "abgerechnet":
      return "invoice_created";
    case "invoice_paid":
    case "invoice-paid":
    case "paid":
      return "invoice_paid";
    case "archived":
      return "archived";
    default:
      return key || "draft";
  }
}

function timesheetLabel(ts: AuftragTimesheetSummary | AuftragTimesheetNumber): string {
  const display =
    (ts as AuftragTimesheetSummary).display_number ||
    (ts as AuftragTimesheetNumber).display_number ||
    null;
  const number =
    (ts as AuftragTimesheetSummary).timesheet_number ||
    (ts as AuftragTimesheetNumber).timesheet_number ||
    (ts as AuftragTimesheetSummary).number ||
    (ts as AuftragTimesheetNumber).number ||
    (ts as AuftragTimesheetSummary).id;
  return display ? String(display) : number != null ? String(number) : "—";
}

function timesheetStatusOf(ts: AuftragTimesheetSummary | AuftragTimesheetNumber): TimesheetStatus | string {
  const raw =
    (ts as AuftragTimesheetSummary).status_display ||
    (ts as AuftragTimesheetSummary).status ||
    (ts as AuftragTimesheetNumber).status ||
    "draft";
  return normalizeTimesheetStatus(raw);
}

/**
 * Liefert den darzustellenden Wert einer Zelle. Werte werden als String
 * formatiert (oder als `null`, wenn die Zelle leer ist), damit die Tabelle
 * eindeutig "—" anzeigen kann. Für `timesheets` werden zusätzlich Badges
 * geliefert, damit die Tabelle sie als farbige Pill-Buttons rendern kann.
 */
export function renderCellValue(
  row: Order,
  key: string,
  heroFields: HeroCustomField[],
): CellValue {
  switch (key) {
    case "auftragsnummer": {
      const num = row.auftragsnummer || "";
      return { value: num || null, mono: true };
    }
    case "kunde": {
      const appName = String(row.gp_firmenname || "").trim();
      const folder = String(row.kunde || "").trim();
      const hero = String(row.hero_kunde || "").trim();
      // Der Geschäftspartner am Projekt (auch nur in der App) geht vor dem
      // Ordnernamen. „Verschiedene“ ist der Sammelordner, nicht die Firma.
      const name = appName
        ? appName
        : row.use_hero_customer_name_in_projects
          ? hero || folder
          : folder || hero;
      return { value: name || null };
    }
    case "ansprechpartner":
      return { value: row.ansprechpartner || null };
    case "ort":
      return { value: row.ort || null };
    case "ort_zusatz":
      return { value: row.ort_zusatz || null };
    case "beschreibung":
      return { value: row.beschreibung || null };
    case "historische_rechnungsnummer":
      return { value: row.historische_rechnungsnummer || null, mono: true };
    case "historische_lieferscheinnummer":
      return { value: row.historische_lieferscheinnummer || null, mono: true };
    case "historische_info":
      return { value: row.historische_info || null };
    case "status": {
      const workflow = projectWorkflowStatus(row);
      return {
        value: workflow.label,
        statusKey: workflow.key,
        secondaryStatusKey: workflow.secondary?.key,
        secondaryStatusLabel: workflow.secondary?.label,
      };
    }
    case "execution_class": {
      const exc = String(row.execution_class || "").trim().toUpperCase();
      if (exc !== "EXC1" && exc !== "EXC2") return { value: null };
      return { value: exc, executionClassBadge: true };
    }
    case "bestellnummer":
      return {
        value: row.bestellnummer || null,
        mono: true,
        bestellnummerQuelle: row.bestellnummer_quelle || null,
      };
    case "kundenreferenz":
      return { value: row.kundenreferenz || null, mono: true };
    case "leitweg_id":
      return { value: row.leitweg_id || null, mono: true };
    case "hours_usage":
      // Spezialzelle – HoursUsageCell in ProjectTable (Festpreis: Ist vs. Kalk.)
      return { value: null };
    case "booked_hours":
      // Spezialzelle – BookedHoursCell in ProjectTable
      return { value: null };
    case "timesheets": {
      const detailed = Array.isArray(row.timesheets) ? row.timesheets : [];
      if (detailed.length) {
        const badges: TimesheetBadge[] = detailed.map((ts) => ({
          label: timesheetLabel(ts),
          status: timesheetStatusOf(ts),
        }));
        return { value: badges.map((b) => b.label).join(", "), badges };
      }
      const list = Array.isArray(row.timesheet_numbers) ? row.timesheet_numbers : [];
      if (!list.length) return { value: null };
      const badges: TimesheetBadge[] = list.map((ts) => ({
        label: timesheetLabel(ts),
        status: timesheetStatusOf(ts),
      }));
      return { value: badges.map((b) => b.label).join(", "), badges };
    }
    case "hero":
    case "open":
    case "cover_print_default":
      // Aktionsspalten rendern keinen Text – die Tabelle stellt eigene Buttons.
      return { value: null };
    default: {
      // Hero-Custom-Field: cf_<schemaId>_<uuid>
      if (key.startsWith("cf_")) {
        const field = heroFields.find((f) => heroCustomFieldKey(f) === key);
        if (!field) return { value: null };

        // Auftragsart-Spalte: App-Angebot hat Vorrang und wird als Badge gezeigt.
        if (isAuftragsartHeroFieldLabel(field.label)) {
          const appLabel = formatAuftragsartAppLabel(row.auftragsart_app);
          const badgeKind = auftragsartAppBadgeKind(appLabel);
          if (appLabel && badgeKind) {
            return { value: appLabel, auftragsartBadge: badgeKind };
          }
        }

        // Kalk-Stunden: App-Angebot (nur Festpreis) hat Vorrang vor Hero-CF.
        if (orderUsesAppKalkStunden(row)) {
          const split = splitAppKalkStunden(resolveAppKalkStunden(row));
          if (isWorkshopHoursHeroFieldLabel(field.label)) {
            return { value: split.workshop != null ? formatHours(split.workshop) : null };
          }
          if (isMontageHoursHeroFieldLabel(field.label)) {
            return { value: split.montage != null ? formatHours(split.montage) : null };
          }
        }

        const match = findHeroCustomFieldMatch(
          row.hero_custom_fields,
          field.schemaId,
          field.uuid,
        );
        const raw = match?.value;
        if (raw === undefined || raw === null || raw === "") return { value: null };
        if (typeof raw === "boolean") return { value: raw ? "✓" : null };
        return { value: String(raw) };
      }
      return { value: null };
    }
  }
}

/* ───────────────────── Spaltenbreiten-Helfer ───────────────────── */

export function clampColumnWidth(width: number): number {
  if (!Number.isFinite(width)) return 0;
  return Math.max(
    PROJECT_COL_MIN_WIDTH,
    Math.min(PROJECT_COL_MAX_WIDTH, Math.round(width)),
  );
}

export function getColumnDefaultWidth(key: string): number {
  if (isProjectActionColumn(key)) return PROJECT_ACTION_COL_WIDTH;
  switch (key) {
    case "auftragsnummer":
      return 140;
    case "kunde":
      return 220;
    case "ansprechpartner":
      return 180;
    case "ort":
    case "ort_zusatz":
      return 140;
    case "beschreibung":
      return 320;
    case "historische_rechnungsnummer":
    case "historische_lieferscheinnummer":
      return 140;
    case "historische_info":
      return 220;
    case "status":
      return 110;
    case "execution_class":
      return 72;
    case "bestellnummer":
    case "kundenreferenz":
    case "leitweg_id":
      return 160;
    case "hours_usage":
      return 72;
    case "booked_hours":
      return 120;
    case "timesheets":
      return 200;
    default:
      return 140;
  }
}
